import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { formatCurrency as formatCurrencyUtil, formatCompact, formatAmount, getCurrencySymbol, CURRENCIES } from '@/utils/currency';
import { fetchExchangeRates, convertAmount as convertCurrencyAmount } from '@/services/exchangeRates';

const BudgetContext = createContext(null);

// Category configurations with colors
export const CATEGORIES = {
  'Food & Dining': { icon: 'Utensils', color: 'category-food' },
  'Transportation': { icon: 'Car', color: 'category-transport' },
  'Utilities': { icon: 'Zap', color: 'category-utilities' },
  'Entertainment': { icon: 'Gamepad2', color: 'category-entertainment' },
  'Shopping': { icon: 'ShoppingBag', color: 'category-shopping' },
  'Healthcare': { icon: 'Heart', color: 'category-healthcare' },
  'Education': { icon: 'GraduationCap', color: 'category-education' },
  'Savings': { icon: 'PiggyBank', color: 'category-savings' },
  'Other': { icon: 'MoreHorizontal', color: 'category-other' },
};

export const INCOME_CATEGORIES = {
  'Salary': { icon: 'Briefcase', color: 'success' },
  'Freelance': { icon: 'Laptop', color: 'secondary' },
  'Investment': { icon: 'TrendingUp', color: 'primary' },
  'Other Income': { icon: 'DollarSign', color: 'accent' },
};

// Default budget goals
const DEFAULT_BUDGET_GOALS = {
  'Food & Dining': 500,
  'Transportation': 200,
  'Utilities': 150,
  'Entertainment': 100,
  'Shopping': 200,
  'Healthcare': 100,
  'Education': 150,
  'Savings': 500,
  'Other': 100,
};

export const BudgetProvider = ({ children }) => {
  const { isAuthenticated, authFetch, user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [budgetGoals, setBudgetGoals] = useState(DEFAULT_BUDGET_GOALS);
  const [customCategoriesMeta, setCustomCategoriesMeta] = useState({}); // {CategoryName: {icon: 'Home', color: '#6366f1'}}
  const [settings, setSettings] = useState({
    currency: 'CAD',
    darkMode: false,
  });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Load exchange rates when authenticated
  useEffect(() => {
    if (isAuthenticated && authFetch) {
      loadExchangeRates();
    }
  }, [isAuthenticated, authFetch]);

  const loadExchangeRates = async () => {
    try {
      const rates = await fetchExchangeRates('USD', authFetch);
      setExchangeRates(rates);
    } catch (error) {
      console.error('Failed to load exchange rates:', error);
    }
  };

  // Load data from API when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Fetch transactions, budget goals, and settings in parallel
      const [transRes, goalsRes, settingsRes] = await Promise.all([
        authFetch('/api/transactions'),
        authFetch('/api/budget-goals'),
        authFetch('/api/settings')
      ]);

      if (transRes.ok) {
        const data = await transRes.json();
        setTransactions(data.transactions || []);
      }

      if (goalsRes.ok) {
        const data = await goalsRes.json();
        setBudgetGoals(data.goals || DEFAULT_BUDGET_GOALS);
        // Load custom category metadata (icons, colors)
        if (data.custom_categories_meta) {
          setCustomCategoriesMeta(data.custom_categories_meta);
        }
      }

      let isNewUser = false;
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        // Check if this is a new user (no settings saved yet means default CAD and updated_at is very recent)
        // We'll use a flag in settings or check if currency was never explicitly set
        isNewUser = !data.onboarding_completed;
        
        setSettings({
          currency: data.currency || 'CAD',
          darkMode: data.dark_mode || false
        });
        if (data.dark_mode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      } else {
        // No settings exist - definitely a new user
        isNewUser = true;
      }
      
      setNeedsOnboarding(isNewUser);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Complete onboarding with selected currency
  const completeOnboarding = async (currency) => {
    try {
      const response = await authFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          currency, 
          dark_mode: settings.darkMode,
          onboarding_completed: true 
        })
      });
      
      if (response.ok) {
        setSettings(prev => ({ ...prev, currency }));
        setNeedsOnboarding(false);
        // Reload exchange rates for new currency
        await loadExchangeRates();
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  // Convert transaction amount to user's selected currency
  const convertTransactionAmount = useCallback((transaction) => {
    if (!exchangeRates || !transaction) return transaction.amount;
    
    const originalCurrency = transaction.original_currency || 'CAD';
    const targetCurrency = settings.currency;
    
    if (originalCurrency === targetCurrency) {
      return transaction.amount;
    }
    
    return convertCurrencyAmount(transaction.amount, originalCurrency, targetCurrency, exchangeRates);
  }, [exchangeRates, settings.currency]);

  // Convert any amount from one currency to user's selected currency
  // Used by components that have amounts stored in a specific currency
  const convertToSelectedCurrency = useCallback((amount, fromCurrency = 'CAD') => {
    if (!exchangeRates || !amount) return amount;
    
    const targetCurrency = settings.currency;
    
    if (fromCurrency === targetCurrency) {
      return amount;
    }
    
    return convertCurrencyAmount(amount, fromCurrency, targetCurrency, exchangeRates);
  }, [exchangeRates, settings.currency]);

  // Get transactions with amounts converted to selected currency
  const convertedTransactions = useMemo(() => {
    if (!exchangeRates) return transactions;
    
    return transactions.map(t => ({
      ...t,
      amount: convertTransactionAmount(t), // Use converted amount as primary
      originalAmount: t.amount,
      displayAmount: convertTransactionAmount(t),
    }));
  }, [transactions, exchangeRates, convertTransactionAmount]);

  // Add a new transaction
  const addTransaction = useCallback(async (transaction) => {
    try {
      // Add current currency as original_currency
      const transactionWithCurrency = {
        ...transaction,
        original_currency: settings.currency
      };
      
      const response = await authFetch('/api/transactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transactionWithCurrency)
      });

      if (response.ok) {
        const newTransaction = await response.json();
        setTransactions((prev) => [newTransaction, ...prev]);
        return newTransaction;
      }
    } catch (error) {
      console.error('Error adding transaction:', error);
    }
    return null;
  }, [authFetch, settings.currency]);

  // Add multiple transactions (from receipt scanner)
  const addTransactionsBulk = useCallback(async (transactionsList) => {
    try {
      // Add current currency to all transactions
      const transactionsWithCurrency = transactionsList.map(t => ({
        ...t,
        original_currency: settings.currency
      }));
      
      const response = await authFetch('/api/transactions/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactions: transactionsWithCurrency })
      });

      if (response.ok) {
        const data = await response.json();
        setTransactions((prev) => [...data.created, ...prev]);
        return data.created;
      }
    } catch (error) {
      console.error('Error adding transactions:', error);
    }
    return [];
  }, [authFetch, settings.currency]);

  // Update a transaction
  const updateTransaction = useCallback(async (id, updates) => {
    try {
      const response = await authFetch(`/api/transactions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updated = await response.json();
        setTransactions((prev) =>
          prev.map((t) => (t.id === id ? updated : t))
        );
        return updated;
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
    }
    return null;
  }, [authFetch]);

  // Delete a transaction
  const deleteTransaction = useCallback(async (id) => {
    try {
      const response = await authFetch(`/api/transactions/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setTransactions((prev) => prev.filter((t) => t.id !== id));
        return true;
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
    return false;
  }, [authFetch]);

  // Update budget goal - supports both simple amount and object with metadata for custom categories
  // For predefined categories: updateBudgetGoal('Food & Dining', 500)
  // For custom categories: updateBudgetGoal('Rent', { budget: 1000, icon: 'Home', color: '#6366f1' })
  const updateBudgetGoal = useCallback(async (category, amountOrMeta) => {
    const isPredefined = ['Food & Dining', 'Transportation', 'Utilities', 'Entertainment', 
                         'Shopping', 'Healthcare', 'Education', 'Savings', 'Other'].includes(category);
    
    // For predefined categories, just update the amount
    if (isPredefined || typeof amountOrMeta === 'number') {
      const amount = typeof amountOrMeta === 'number' ? amountOrMeta : amountOrMeta.budget;
      const newGoals = { ...budgetGoals, [category]: amount };
      setBudgetGoals(newGoals);

      try {
        await authFetch('/api/budget-goals', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ goals: newGoals })
        });
      } catch (error) {
        console.error('Error updating budget goal:', error);
      }
    } else {
      // For custom categories with metadata
      const { budget, icon, color } = amountOrMeta;
      const newGoals = { ...budgetGoals, [category]: budget };
      setBudgetGoals(newGoals);
      
      // Update custom categories meta locally
      const newMeta = { ...customCategoriesMeta, [category]: { icon, color } };
      setCustomCategoriesMeta(newMeta);

      try {
        // Send full metadata for custom category
        await authFetch('/api/budget-goals', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            goals: { 
              ...budgetGoals,
              [category]: { budget, icon, color }
            }
          })
        });
      } catch (error) {
        console.error('Error updating budget goal:', error);
      }
    }
  }, [authFetch, budgetGoals, customCategoriesMeta]);

  // Delete a custom category
  const deleteCustomCategory = useCallback(async (category) => {
    try {
      const response = await authFetch(`/api/budget-goals/category/${encodeURIComponent(category)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update local state
        setBudgetGoals(data.goals || {});
        setCustomCategoriesMeta(data.custom_categories_meta || {});
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting custom category:', error);
      return false;
    }
  }, [authFetch]);

  // Toggle dark mode
  const toggleDarkMode = useCallback(async () => {
    const newDarkMode = !settings.darkMode;
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    setSettings((prev) => ({ ...prev, darkMode: newDarkMode }));

    try {
      await authFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          currency: settings.currency,
          dark_mode: newDarkMode 
        })
      });
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  }, [authFetch, settings]);

  // Update settings (e.g., currency)
  const updateSettings = useCallback(async (newSettings) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);

    try {
      await authFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          currency: updatedSettings.currency,
          dark_mode: updatedSettings.darkMode 
        })
      });
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  }, [authFetch, settings]);

  // Get transactions for a specific month (with converted amounts)
  const getMonthTransactions = useCallback(
    (date = currentMonth) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const filtered = convertedTransactions.filter((t) => {
        const dateParts = t.date.split('-');
        const tYear = parseInt(dateParts[0], 10);
        const tMonth = parseInt(dateParts[1], 10) - 1;
        return tYear === year && tMonth === month;
      });
      // Return transactions with displayAmount as the primary amount for calculations
      return filtered.map(t => ({
        ...t,
        amount: t.displayAmount !== undefined ? t.displayAmount : t.amount
      }));
    },
    [convertedTransactions, currentMonth]
  );

  // Calculate totals for current month (using converted amounts)
  const getMonthTotals = useCallback(
    (date = currentMonth) => {
      const monthTransactions = getMonthTransactions(date);
      const income = monthTransactions
        .filter((t) => t.type === 'income')
        .reduce((sum, t) => sum + t.amount, 0);
      const expenses = monthTransactions
        .filter((t) => t.type === 'expense')
        .reduce((sum, t) => sum + t.amount, 0);
      return { income, expenses, balance: income - expenses };
    },
    [getMonthTransactions, currentMonth]
  );

  // Get category spending for current month (using converted amounts)
  const getCategorySpending = useCallback(
    (date = currentMonth) => {
      const monthTransactions = getMonthTransactions(date);
      const spending = {};
      
      Object.keys(CATEGORIES).forEach((cat) => {
        spending[cat] = monthTransactions
          .filter((t) => t.type === 'expense' && t.category === cat)
          .reduce((sum, t) => sum + t.amount, 0);
      });
      
      // Also add any custom categories from transactions that aren't in the default list
      monthTransactions.forEach(t => {
        if (t.type === 'expense' && t.category && !spending.hasOwnProperty(t.category)) {
          spending[t.category] = monthTransactions
            .filter((tx) => tx.type === 'expense' && tx.category === t.category)
            .reduce((sum, tx) => sum + tx.amount, 0);
        }
      });
      
      return spending;
    },
    [getMonthTransactions, currentMonth]
  );

  // Get unique categories from all transactions and budget goals
  const getUniqueCategories = useCallback(() => {
    const expenseCats = new Set(Object.keys(CATEGORIES));
    const incomeCats = new Set(Object.keys(INCOME_CATEGORIES));
    
    // Add categories from transactions
    transactions.forEach(t => {
      if (t.category) {
        if (t.type === 'expense') expenseCats.add(t.category);
        else incomeCats.add(t.category);
      }
    });
    
    // Add custom categories from budget goals (these are expense categories)
    Object.keys(budgetGoals).forEach(cat => {
      if (!INCOME_CATEGORIES[cat]) {
        expenseCats.add(cat);
      }
    });
    
    return {
      expense: Array.from(expenseCats),
      income: Array.from(incomeCats)
    };
  }, [transactions, budgetGoals]);

  // Get spending by category with budget comparison
  const getCategoryBudgetStatus = useCallback(
    (date = currentMonth) => {
      const spending = getCategorySpending(date);
      return Object.keys(CATEGORIES).map((category) => ({
        category,
        spent: spending[category] || 0,
        budget: budgetGoals[category] || 0,
        percentage: budgetGoals[category]
          ? Math.min(((spending[category] || 0) / budgetGoals[category]) * 100, 100)
          : 0,
        remaining: Math.max((budgetGoals[category] || 0) - (spending[category] || 0), 0),
        isOverBudget: (spending[category] || 0) > (budgetGoals[category] || 0),
      }));
    },
    [getCategorySpending, budgetGoals, currentMonth]
  );

  // Get monthly data for charts (last 6 months)
  const getMonthlyChartData = useCallback(() => {
    const months = [];
    const today = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const totals = getMonthTotals(date);
      months.push({
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        fullMonth: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        year: date.getFullYear(),
        monthIndex: date.getMonth(),
        income: totals.income,
        expenses: totals.expenses,
        balance: totals.balance,
      });
    }
    
    return months;
  }, [getMonthTotals]);

  // Get pie chart data for expenses
  const getExpensePieData = useCallback(
    (date = currentMonth) => {
      const spending = getCategorySpending(date);
      return Object.entries(spending)
        .filter(([_, amount]) => amount > 0)
        .map(([category, amount]) => ({
          name: category,
          value: amount,
          color: CATEGORIES[category]?.color || 'muted',
        }));
    },
    [getCategorySpending, currentMonth]
  );

  // Export data as JSON
  const exportData = useCallback(() => {
    const data = {
      transactions,
      budgetGoals,
      settings,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `budget-data-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [transactions, budgetGoals, settings]);

  // Import data from JSON (recreates transactions via API)
  const importData = useCallback(async (jsonData) => {
    try {
      const data = JSON.parse(jsonData);
      
      // Import transactions
      if (data.transactions && data.transactions.length > 0) {
        const transactionsToImport = data.transactions.map(t => ({
          type: t.type,
          amount: t.amount,
          category: t.category,
          description: t.description,
          date: t.date
        }));
        await addTransactionsBulk(transactionsToImport);
      }
      
      // Import budget goals
      if (data.budgetGoals) {
        await authFetch('/api/budget-goals', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ goals: data.budgetGoals })
        });
        setBudgetGoals(data.budgetGoals);
      }
      
      // Import settings
      if (data.settings) {
        await authFetch('/api/settings', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            currency: data.settings.currency || 'USD',
            dark_mode: data.settings.darkMode || false
          })
        });
        setSettings(data.settings);
        if (data.settings.darkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
      
      // Reload data
      await loadData();
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  }, [authFetch, addTransactionsBulk]);

  // Currency formatting functions that use current settings
  const formatCurrency = useCallback((amount, options = {}) => {
    return formatCurrencyUtil(amount, settings.currency, options);
  }, [settings.currency]);

  const formatMoney = useCallback((amount) => {
    return formatAmount(amount, settings.currency);
  }, [settings.currency]);

  const formatMoneyCompact = useCallback((amount) => {
    return formatCompact(amount, settings.currency);
  }, [settings.currency]);

  const currencySymbol = useMemo(() => {
    return getCurrencySymbol(settings.currency);
  }, [settings.currency]);

  // Real-time update handlers for external sync
  const handleRealtimeTransactionUpdate = useCallback(({ eventType, newRecord, oldRecord }) => {
    if (eventType === 'INSERT' && newRecord) {
      setTransactions(prev => {
        // Check if already exists
        if (prev.some(t => t.id === newRecord.id)) return prev;
        return [newRecord, ...prev];
      });
    } else if (eventType === 'UPDATE' && newRecord) {
      setTransactions(prev => prev.map(t => t.id === newRecord.id ? newRecord : t));
    } else if (eventType === 'DELETE' && oldRecord) {
      setTransactions(prev => prev.filter(t => t.id !== oldRecord.id));
    }
  }, []);

  const handleRealtimeSettingsUpdate = useCallback((payload) => {
    const newSettings = payload.new;
    if (newSettings) {
      setSettings({
        currency: newSettings.currency || 'CAD',
        darkMode: newSettings.dark_mode || false,
      });
      if (newSettings.dark_mode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, []);

  const value = {
    transactions: convertedTransactions, // Export converted transactions instead of raw
    rawTransactions: transactions, // Keep raw transactions available if needed
    budgetGoals,
    customCategoriesMeta, // Custom category icons and colors
    settings,
    currentMonth,
    isLoading,
    setCurrentMonth,
    addTransaction,
    addTransactionsBulk,
    updateTransaction,
    deleteTransaction,
    updateBudgetGoal,
    toggleDarkMode,
    getMonthTransactions,
    getMonthTotals,
    getCategorySpending,
    getCategoryBudgetStatus,
    getMonthlyChartData,
    getExpensePieData,
    getUniqueCategories,
    exportData,
    importData,
    reloadData: loadData,
    updateSettings,
    // Currency utilities
    formatCurrency,
    formatMoney,
    formatMoneyCompact,
    currencySymbol,
    // Exchange rates
    exchangeRates,
    reloadExchangeRates: loadExchangeRates,
    convertToSelectedCurrency,
    // Onboarding
    needsOnboarding,
    completeOnboarding,
    // Real-time handlers
    handleRealtimeTransactionUpdate,
    handleRealtimeSettingsUpdate,
    // Custom categories
    deleteCustomCategory,
  };

  return (
    <BudgetContext.Provider value={value}>{children}</BudgetContext.Provider>
  );
};

export const useBudget = () => {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within a BudgetProvider');
  }
  return context;
};
